import { useLayoutEffect, useMemo, useState } from 'react'
import { Icon } from './icon'
import { Popover } from './popover'

// Ukrainian month names (nominative — calendar-header form) and Monday-first
// weekday headers (UA/EU week starts on Monday).
const MONTHS_UK = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
]
const WEEKDAYS_UK = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд']

/** Local yyyy-mm-dd (avoids the UTC off-by-one of toISOString). */
function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Parse 'YYYY-MM-DD' into a local midnight Date, or null if malformed. */
function parseYmd(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** Monday-based weekday index (Mon=0 … Sun=6). */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7
}

/** Step a year/month pair by `delta` months, wrapping the month into 0..11. */
function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 }
}

type Props = {
  value: string
  onChange: (value: string) => void
  /** Inclusive lower/upper bound (yyyy-mm-dd); days outside are not selectable. */
  min?: string
  max?: string
  disabled?: boolean
  /** Applied to the trigger so it matches surrounding fields (e.g. "input"). */
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  id?: string
  /** Show a clear ("Очистити") action and allow emptying the value. */
  clearable?: boolean
  /** Fixed trigger width for inline use (filter bars). Omit to fill its container. */
  width?: number
}

// Calendar date picker — a drop-in replacement for `<input type="date">` that
// opens a click-to-select month grid instead of relying on the native control.
// Keeps the same yyyy-mm-dd string contract, so call sites only swap the element
// and the onChange signature (value string instead of an event).
export function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled = false,
  className,
  style,
  placeholder = 'Оберіть дату',
  id,
  clearable,
  width,
}: Props) {
  const [open, setOpen] = useState(false)
  const selected = parseYmd(value)
  const today = new Date()
  const todayYmd = ymd(today)
  const [view, setView] = useState(() => {
    const base = selected ?? today
    return { year: base.getFullYear(), month: base.getMonth() }
  })

  // Re-centre the calendar on the selected month (or today) each time it opens.
  useLayoutEffect(() => {
    if (!open) return
    const base = parseYmd(value) ?? new Date()
    setView({ year: base.getFullYear(), month: base.getMonth() })
  }, [open, value])

  const isDisabledDay = (d: string) =>
    (min != null && min !== '' && d < min) || (max != null && max !== '' && d > max)

  // A fixed 6-week grid (42 cells) starting on the Monday on/before the 1st, so
  // the popover height never jumps between months.
  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1)
    const start = new Date(first)
    start.setDate(1 - mondayIndex(first))
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [view])

  const choose = (d: Date) => {
    onChange(ymd(d))
    setOpen(false)
  }
  const todayDisabled = isDisabledDay(todayYmd)

  return (
    <span
      style={{
        position: 'relative',
        display: width != null ? 'inline-block' : 'block',
        width: width ?? '100%',
      }}
    >
      <button
        id={id}
        type="button"
        className={className}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          fontFamily: 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...style,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selected ? 'var(--fg-1)' : 'var(--fg-4)',
          }}
        >
          {value || placeholder}
        </span>
        <Icon name="calendar" size={13} color="var(--fg-4)" />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} width={264}>
        <div style={{ padding: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ display: 'flex', gap: 2 }}>
              <NavBtn
                icon="chevrons-left"
                label="Попередній рік"
                onClick={() => setView((v) => addMonths(v.year, v.month, -12))}
              />
              <NavBtn
                icon="chevron-left"
                label="Попередній місяць"
                onClick={() => setView((v) => addMonths(v.year, v.month, -1))}
              />
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-1)' }}>
              {MONTHS_UK[view.month]} {view.year}
            </span>
            <span style={{ display: 'flex', gap: 2 }}>
              <NavBtn
                icon="chevron-right"
                label="Наступний місяць"
                onClick={() => setView((v) => addMonths(v.year, v.month, 1))}
              />
              <NavBtn
                icon="chevrons-right"
                label="Наступний рік"
                onClick={() => setView((v) => addMonths(v.year, v.month, 12))}
              />
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2,
              marginBottom: 2,
            }}
          >
            {WEEKDAYS_UK.map((w) => (
              <div
                key={w}
                style={{ textAlign: 'center', fontSize: 10, color: 'var(--fg-4)', padding: '2px 0' }}
              >
                {w}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d) => {
              const dYmd = ymd(d)
              const inMonth = d.getMonth() === view.month
              const isSelected = value === dYmd
              const isToday = dYmd === todayYmd
              const dayDisabled = isDisabledDay(dYmd)
              return (
                <button
                  key={dYmd}
                  type="button"
                  disabled={dayDisabled}
                  onClick={() => choose(d)}
                  onMouseEnter={(e) => {
                    if (!isSelected && !dayDisabled) e.currentTarget.style.background = 'var(--bg-3)'
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'transparent'
                  }}
                  style={{
                    height: 30,
                    borderRadius: 6,
                    border: 0,
                    fontFamily: 'inherit',
                    fontSize: 12,
                    cursor: dayDisabled ? 'not-allowed' : 'pointer',
                    background: isSelected ? 'var(--accent)' : 'transparent',
                    color: isSelected
                      ? 'var(--accent-fg)'
                      : inMonth
                        ? 'var(--fg-1)'
                        : 'var(--fg-4)',
                    opacity: dayDisabled ? 0.4 : 1,
                    fontWeight: isSelected || isToday ? 600 : 400,
                    outline: isToday && !isSelected ? '1px solid var(--border-strong)' : 'none',
                    outlineOffset: -1,
                  }}
                >
                  {d.getDate()}
                </button>
              )
            })}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: 8,
            }}
          >
            <button
              type="button"
              disabled={todayDisabled}
              onClick={() => !todayDisabled && choose(today)}
              style={{
                background: 'transparent',
                border: 0,
                color: todayDisabled ? 'var(--fg-4)' : 'var(--accent)',
                fontSize: 12,
                fontFamily: 'inherit',
                cursor: todayDisabled ? 'not-allowed' : 'pointer',
                padding: '2px 4px',
              }}
            >
              Сьогодні
            </button>
            {clearable && value && (
              <button
                type="button"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                }}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--fg-3)',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  padding: '2px 4px',
                }}
              >
                Очистити
              </button>
            )}
          </div>
        </div>
      </Popover>
    </span>
  )
}

function NavBtn({
  icon,
  label,
  onClick,
}: {
  icon: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-3)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: 6,
        border: 0,
        background: 'transparent',
        color: 'var(--fg-2)',
        cursor: 'pointer',
      }}
    >
      <Icon name={icon} size={14} />
    </button>
  )
}
